import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { RelativeTime } from "@/components/data";
import { canaisWhatsApp, cargoAtualTexto, experienciaTexto, whatsAppWebUrl, type CanaisWhatsApp } from "./contatoUtils";
import { toneTextClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import type { LinkedInRapido, WhatsAppRapido } from "@/types/freelancer-painel";
import type { WhatsAppAguardando } from "@/types/whatsapp";

const AGUARDANDO_LABEL: Record<WhatsAppAguardando, string> = {
  equipe: "Aguardando a equipe",
  contato: "Aguardando o freelancer",
  ninguem: "Sem pendência",
};

function WhatsAppResumoTooltip({ resumo }: { resumo: WhatsAppRapido | null | undefined }) {
  if (!resumo?.tem_conversa) {
    return <p className="text-xs">Sem conversa registrada no WhatsApp.</p>;
  }
  return (
    <div className="max-w-xs space-y-1 text-xs">
      {resumo.status_atual ? (
        <p>
          <span className="font-medium">Status da conversa:</span> {resumo.status_atual}
        </p>
      ) : (
        <p>Conversa registrada, ainda sem resumo.</p>
      )}
      {resumo.aguardando && <p>{AGUARDANDO_LABEL[resumo.aguardando as WhatsAppAguardando] ?? resumo.aguardando}</p>}
      <p className="text-muted-foreground">
        Última mensagem dele:{" "}
        {resumo.ultima_mensagem_freelancer_at ? <RelativeTime value={resumo.ultima_mensagem_freelancer_at} /> : "nenhuma"}
      </p>
    </div>
  );
}

/** Itens do menu: cada canal habilitado só quando existe, com o motivo quando não. */
export type CanalWhatsApp = "whatsapp_web" | "jetsales";

export function WhatsAppMenuItems({ canais, onOpen }: { canais: CanaisWhatsApp; onOpen?: (canal: CanalWhatsApp) => void }) {
  return (
    <>
      <DropdownMenuItem disabled={!canais.numero} asChild={!!canais.numero}>
        {canais.numero ? (
          <a href={whatsAppWebUrl(canais.numero)} target="_blank" rel="noopener noreferrer" onClick={() => onOpen?.("whatsapp_web")}>
            Abrir no WhatsApp Web
          </a>
        ) : (
          <span>WhatsApp Web (sem telefone válido)</span>
        )}
      </DropdownMenuItem>
      <DropdownMenuItem disabled={!canais.jetsalesUrl} asChild={!!canais.jetsalesUrl}>
        {canais.jetsalesUrl ? (
          <a href={canais.jetsalesUrl} target="_blank" rel="noopener noreferrer" onClick={() => onOpen?.("jetsales")}>
            Abrir na JetSales
          </a>
        ) : (
          <span>JetSales (sem ticket aberto)</span>
        )}
      </DropdownMenuItem>
    </>
  );
}

export function WhatsAppIcone({
  resumo,
  telefone,
  className,
  onOpen,
}: {
  resumo: WhatsAppRapido | null | undefined;
  telefone?: string | null;
  className?: string;
  onOpen?: (canal: CanalWhatsApp) => void;
}) {
  const canais = canaisWhatsApp(resumo, telefone);
  const semCanal = !canais.numero && !canais.jetsalesUrl;
  const [menuOpen, setMenuOpen] = useState(false);
  const tom = resumo?.tem_conversa ? toneTextClasses.success : semCanal ? "text-muted-foreground/30" : "text-muted-foreground";

  return (
    <TooltipProvider>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <Tooltip open={menuOpen ? false : undefined}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", tom, className)}
                aria-label="WhatsApp"
                data-testid="whatsapp-icone"
                data-conversa={resumo?.tem_conversa ? "sim" : "nao"}
                onClick={(event) => event.stopPropagation()}
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <WhatsAppResumoTooltip resumo={resumo} />
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Conversar pelo WhatsApp</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <WhatsAppMenuItems canais={canais} onOpen={onOpen} />
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

function LinkedInResumoTooltip({ linkedin }: { linkedin: LinkedInRapido | null | undefined }) {
  if (!linkedin || linkedin.url_status === "vazio") return <p className="text-xs">LinkedIn não cadastrado.</p>;
  if (linkedin.url_status !== "ok") return <p className="text-xs">O link cadastrado não é um perfil válido do LinkedIn.</p>;
  const cargo = cargoAtualTexto(linkedin);
  const experiencia = experienciaTexto(linkedin);
  if (!linkedin.headline && !cargo && !experiencia) {
    return <p className="text-xs">LinkedIn ainda não lido pela plataforma.</p>;
  }
  return (
    <div className="max-w-xs space-y-1 text-xs">
      {linkedin.headline && <p className="font-medium">{linkedin.headline}</p>}
      {cargo && <p>{cargo}</p>}
      {experiencia && <p>{experiencia}</p>}
      {linkedin.aberto_a_oportunidades && <p className={toneTextClasses.success}>Aberto a oportunidades</p>}
      {linkedin.atualizado_em && (
        <p className="text-muted-foreground">
          Lido <RelativeTime value={linkedin.atualizado_em} />
        </p>
      )}
    </div>
  );
}

export function LinkedInIcone({
  freelancerId,
  url,
  resumo,
  iconClassName,
  onOpen,
}: {
  freelancerId: string;
  url: string | null | undefined;
  resumo: LinkedInRapido | null | undefined;
  iconClassName?: string;
  onOpen?: () => void;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-testid="linkedin-icone">
            <FreelancerLinkedInButton freelancerId={freelancerId} url={url} hideWhenEmpty={false} nativeTitle={false} iconClassName={iconClassName} onOpen={onOpen} />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <LinkedInResumoTooltip linkedin={resumo} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
